/**
 * Utility functions for classifying and processing session chat turns.
 * Used by SessionChatHistory to render booking-related turns as rich cards.
 */
import { parseDynamoField } from "./dynamoDbParser";

/**
 * Extract search dates from SHOW_OFFERS assistant content.
 * Content format: "Searching for rooms from 2026-03-25 to 2026-03-26.\n\nI found 6 room(s) available:"
 */
const extractDatesFromContent = (content) => {
  if (!content) return null;
  const match = content.match(/from (\d{4}-\d{2}-\d{2}) to (\d{4}-\d{2}-\d{2})/);
  if (match) {
    return { checkIn: match[1], checkOut: match[2] };
  }
  return null;
};

/**
 * Normalise a single turn coming from the getCustomerChats API.
 *
 * Several fields are stored in DynamoDB as String attributes whose value is
 * itself a JSON string containing DynamoDB type descriptors.  This function
 * parses those fields into usable JS objects/arrays and strips wrapping
 * escaped quotes from content.
 */
const normalizeTurn = (turn) => {
  const out = { ...turn };

  if (typeof out.rooms_shown === "string" && out.rooms_shown) {
    out.rooms_shown = parseDynamoField(out.rooms_shown);
  }
  if (typeof out.booking_result === "string" && out.booking_result) {
    out.booking_result = parseDynamoField(out.booking_result);
  }
  if (typeof out.booking_summary === "string" && out.booking_summary) {
    out.booking_summary = parseDynamoField(out.booking_summary);
  }
  if (
    typeof out.hotel_query_analytics === "string" &&
    out.hotel_query_analytics
  ) {
    out.hotel_query_analytics = parseDynamoField(out.hotel_query_analytics);
  }
  if (typeof out.submitted_data === "string" && out.submitted_data) {
    out.submitted_data = parseDynamoField(out.submitted_data);
  }

  // Strip wrapping escaped quotes that some content values carry
  if (
    typeof out.content === "string" &&
    out.content.length >= 2 &&
    out.content.startsWith('"') &&
    out.content.endsWith('"')
  ) {
    out.content = out.content.slice(1, -1);
  }

  return out;
};

/**
 * Classify a conversation turn for rendering.
 *
 * @param {Object} turn - A turn object from the getCustomerChats API
 * @returns {{ type: string, displayData: Object }}
 *   type is one of: 'rooms_shown' | 'offer_selected' | 'booking_confirmed' | 'booking_summary' | 'chat'
 */
export const classifyTurn = (turn) => {
  // Assistant turn with rooms shown
  if (
    turn.role === "assistant" &&
    Array.isArray(turn.rooms_shown) &&
    turn.rooms_shown.length > 0
  ) {
    return {
      type: "rooms_shown",
      displayData: {
        rooms: turn.rooms_shown,
        content: turn.content,
        searchDates: extractDatesFromContent(turn.content),
      },
    };
  }

  // Assistant turn with booking result (completed booking)
  if (turn.role === "assistant" && turn.booking_result) {
    return {
      type: "booking_confirmed",
      displayData: {
        bookingId: turn.booking_result.booking_id,
        roomName: turn.booking_result.room_name,
        checkIn: turn.booking_result.check_in,
        checkOut: turn.booking_result.check_out,
        totalPrice: turn.booking_result.total_price,
        currency: turn.booking_result.currency,
        guests: turn.booking_result.guests,
        children: turn.booking_result.children,
        paymentUrl: turn.booking_result.booking_url,
        content: turn.content,
      },
    };
  }

  // OFFER_SELECTED action (user selected a package/offer from available rooms)
  if (turn.action === "OFFER_SELECTED") {
    const sd = turn.submitted_data || {};
    return {
      type: "offer_selected",
      displayData: {
        roomName: sd.room_name || null,
        packageName: sd.package_name || null,
        totalPrice: sd.total_price,
        currency: sd.currency || null,
        offerId: sd.offer_id || null,
        content: turn.content,
      },
    };
  }

  // Assistant turn with SHOW_SUMMARY action (summary before confirmation)
  if (turn.role === "assistant" && turn.action === "SHOW_SUMMARY") {
    return {
      type: "booking_summary",
      displayData: {
        content: turn.content,
      },
    };
  }

  // Regular chat turn
  return {
    type: "chat",
    displayData: {
      content: turn.content,
      action: turn.action || null,
    },
  };
};

/**
 * Group sorted turns into a timeline of items for rendering.
 * Each item is either a Q&A chat pair or a rich booking card.
 *
 * @param {Array} turns - Array of turn objects sorted by timestamp
 * @returns {Array} Array of timeline items
 */
export const buildTimeline = (turns) => {
  if (!turns || turns.length === 0) return [];

  const sorted = [...turns]
    .map(normalizeTurn)
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  const timeline = [];
  let i = 0;

  while (i < sorted.length) {
    const turn = sorted[i];
    const classified = classifyTurn(turn);

    // OFFER_SELECTED is a user turn rendered as a rich card
    if (classified.type === "offer_selected") {
      // Pair with the next assistant reply if available
      const nextTurn = sorted[i + 1];
      const assistantReply =
        nextTurn && nextTurn.role === "assistant" ? nextTurn.content : null;

      timeline.push({
        itemType: classified.type,
        displayData: classified.displayData,
        timestamp: turn.timestamp,
        action: turn.action,
        assistantReply,
      });
      // Skip the paired assistant reply so it's not rendered twice
      i += assistantReply ? 2 : 1;
      continue;
    }

    // Rich booking cards for assistant turns
    if (
      turn.role === "assistant" &&
      (classified.type === "rooms_shown" ||
        classified.type === "booking_confirmed" ||
        classified.type === "booking_summary")
    ) {
      // Check if the previous timeline item is a user message that pairs with this
      // If so, attach the user question to provide context
      const prevTurn = i > 0 ? sorted[i - 1] : null;
      const userContext =
        prevTurn && prevTurn.role === "user" ? prevTurn.content : null;

      timeline.push({
        itemType: classified.type,
        displayData: classified.displayData,
        timestamp: turn.timestamp,
        action: turn.action,
        userContext,
      });
      i += 1;
      continue;
    }

    // Regular Q&A pairing
    if (turn.role === "user") {
      const next = sorted[i + 1];
      if (next && next.role === "assistant") {
        const nextClassified = classifyTurn(next);

        // If the assistant reply is a rich card, skip the user turn here;
        // it will be shown as userContext on the card in the next iteration.
        if (
          nextClassified.type === "rooms_shown" ||
          nextClassified.type === "booking_confirmed" ||
          nextClassified.type === "booking_summary"
        ) {
          i += 1;
          continue;
        }

        // Normal Q&A pair
        timeline.push({
          itemType: "chat",
          question: turn.content || "",
          answer: next.content || "",
          timestamp: turn.timestamp,
          action: next.action || turn.action || null,
          hotel_query_analytics: next.hotel_query_analytics || null,
        });
        i += 2;
      } else {
        // User message without an assistant reply
        timeline.push({
          itemType: "chat",
          question: turn.content || "",
          answer: "",
          timestamp: turn.timestamp,
          action: turn.action || null,
          hotel_query_analytics: null,
        });
        i += 1;
      }
    } else if (turn.role === "assistant") {
      // Standalone assistant message (no preceding user message)
      timeline.push({
        itemType: "chat",
        question: "",
        answer: turn.content || "",
        timestamp: turn.timestamp,
        action: turn.action || null,
        hotel_query_analytics: turn.hotel_query_analytics || null,
      });
      i += 1;
    } else {
      // Unknown role
      timeline.push({
        itemType: "chat",
        question: "",
        answer: turn.content || "",
        timestamp: turn.timestamp,
        action: turn.action || null,
        hotel_query_analytics: null,
      });
      i += 1;
    }
  }

  return timeline;
};

/**
 * Format a price with currency.
 * @param {number} amount
 * @param {string} currency - e.g. "USD"
 * @returns {string}
 */
export const formatPrice = (amount, currency) => {
  if (amount === undefined || amount === null) return "N/A";
  const num = Number(amount);
  if (isNaN(num)) return String(amount);
  const formatted = num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${currency} ${formatted}` : `$${formatted}`;
};

/**
 * Format a date string for display (e.g. "Mar 25, 2026")
 * @param {string} dateStr
 * @returns {string}
 */
export const formatBookingDate = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    const dateOnlyMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch.map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "N/A";
  }
};
